import React from 'react'
import { useMediaQuery } from 'react-responsive'
import { projectStore } from '~/stores'
import { observer } from '~/ui/component'
import { HBox, VBox } from '~/ui/components'
import { flagSize } from '~/ui/components/LanguageFlag'
import { createUseStyles, layout, ThemeProvider } from '~/ui/styling'
import { screenMinWidths } from '~/ui/styling/layout'
import { height as headerHeight } from '~/ui/layouts/app/AppHeader'
import LanguageSwitcher from '~/ui/layouts/app/LanguageSwitcher'
import DrawerCommonLinks from './DrawerCommonLinks'
import { useDrawer } from './DrawerContext'
import DrawerFooter from './DrawerFooter'
import DrawerHeader from './DrawerHeader'

// Aligns the flag vertically with the hamburger icon it replaces in AppHeader when the drawer is closed.
const languageSwitcherTopPadding = (headerHeight - flagSize.normal) / 2

export interface Props {
  classNames?: React.ClassNamesProp
}

const Drawer = observer('Drawer', (props: Props) => {

  const {isOpen, close} = useDrawer()

  const containerRef = React.useRef<HTMLDivElement>(null)

  const {project} = projectStore

  const isDesktop    = useMediaQuery({minWidth: screenMinWidths.desktop})
  const isWidescreen = useMediaQuery({minWidth: screenMinWidths.widescreen})

  const tabs       = projectStore.tabs
  const hasChatTab = React.useMemo(() => tabs.find(it => it.link.href.startsWith('//chat')), [tabs])
  const collapsed  = hasChatTab ? !isWidescreen : !isDesktop

  const drawerEnabled = project?.feature('drawer')?.enabled && collapsed

  React.useEffect(() => {
    if (!drawerEnabled) {
      close()
    }
  }, [drawerEnabled, close])

  const handleOutsideClick = React.useCallback((event: MouseEvent) => {
    if (!isOpen || containerRef.current == null) { return }

    if (event.target instanceof Node && !containerRef.current.contains(event.target)) {
      close()
    }
  }, [isOpen, close])


  React.useEffect(() => {
    if (!isOpen) { return }
    const id = setTimeout(() => document.addEventListener('click', handleOutsideClick), 0)
    return () => {
      clearTimeout(id)
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [isOpen, handleOutsideClick])

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <ThemeProvider dark>
        <VBox flex classNames={[$.Drawer, props.classNames]} ref={containerRef}>
          <HBox
            paddingHorizontal={layout.padding.m}
            style={{paddingTop: languageSwitcherTopPadding}}
          >
            <LanguageSwitcher/>
          </HBox>
          <DrawerHeader/>
          <VBox flex justify='bottom'>
            <DrawerCommonLinks/>
            <DrawerFooter/>
          </VBox>
        </VBox>
      </ThemeProvider>
    )
  }

  return render()

})

export default Drawer

export const drawerWidth = 300

const useStyles = createUseStyles(theme => ({
  Drawer: {
    width: drawerWidth,
    backgroundColor: theme.colors.bg.dark.alt,
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
  },
}))